// Vue core libraries - Pinia & Vue
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { DateTime } from "luxon";

import pinia from "@/plugins/PiniaPlugin";

// TMI - augmenting stores
import { useApplicationStore } from "@/stores/Application";
import { useAuthStore } from "@/stores/Auth";
import { useCategoryFilter } from "@/composables/filters/category-filter";
import { useCategoriesStore } from "@/stores/Categories";
import { usePermissionsStore } from "@/stores/Permissions";
import { useSectorFilter } from "@/composables/filters/sector-filter";
import { useSectorsStore } from "@/stores/Sectors";
import { useTickerFilter } from "@/composables/filters/ticker-filter";
import { useTickersStore } from "@/stores/Tickers";
import { normalizeArticle } from "@/services/data/sync/articles-sync-coordinator";
import { hydrateContent } from "@/services/data/sync/content-hydration";

import { isFreelyAvailableContent } from "@/utils/access-utils";
import { sortContentItemsInPlace } from "@tmi/utils/sort";
import { formatDateAsISO8601String } from "@tmi/utils/date-time";

import { articles, cache, CacheClosedError } from "@/services/cache";

import type { Articles } from "@tmi/types/api/content-models";
import type {
    Article,
    Category,
    ContentfulArticle,
    ContentfulItem,
    Countable,
    NormalizedContent,
    Product,
    SearchableTicker,
    Sector
} from "@tmi/types/entities";

import { goFetch } from "@/utils/network-utils";
import { clientStateManager } from "@/services/error-services";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
import { shadowSpikeAlertKey } from "@/types/products/product-definitions";
const logger = createDefaultLogger();

// This is implemented as a computed property to avoid unnecessarily calculating it multiple times
const articleCategories = computed< Category[] >( () => {
    const { hasScannerAccess } = storeToRefs( usePermissionsStore( pinia ) );
    const categoryCountMap: Map< string, Countable< Category > > = new Map< string, Countable< Category > >();
    allArticles.value.forEach( ( item: Article ): void => {
        item.categories.forEach( ( category: Category ): void => {
            if ( !categoryCountMap.has( category.slug ) ) {
                if ( !hasScannerAccess.value && shadowSpikeAlertKey === category.slug ) {
                    return;
                }

                categoryCountMap.set( category.slug, {
                    item: category,
                    count: 0
                } );
            }

            categoryCountMap.get( category.slug )!.count++;
        } );
    } );

    const categoriesByObservationCount: Countable< Category >[] = [ ...categoryCountMap.values() ];
    const categoriesByAscendingObservationCount: Countable< Category >[] =
            categoriesByObservationCount.sort( ( container1: Countable< Category >, container2: Countable< Category > ) => container1.count - container2.count );

    return categoriesByAscendingObservationCount.map( ( container: Countable< Category > ) => container.item );
} );

// State values
const isLoading = computed< boolean >( () => loading.value || fetching.value );

const initialized = ref< boolean >( false );
const fetching = ref< boolean >( false );
const loading = ref< boolean >( false );

const allArticlesBySlugMap = new Map< string, Article >();
const allArticlesByIdMap = new Map< string, Article >();
const allArticles = ref< Article[] >( [] );

export const useArticlesStore = defineStore( 'articles', () => {
    // Depends on appStore for authentication state and to signal that data is being loaded
    const appStore = useApplicationStore();
    const { authToken, isAuthenticated } = storeToRefs( useAuthStore() );
    const categoriesStore = useCategoriesStore();
    const permissionsStore = usePermissionsStore();
    const sectorsStore = useSectorsStore();
    const tickersStore = useTickersStore();

    const { includeCategoryFilter, filterOutItemsThatDoNotContainSelectedCategories } = useCategoryFilter();
    const { includeSectorFilter, filterOutContentItemsThatDoNotContainSelectedSectors } = useSectorFilter();
    const { includeTickerFilter, filterOutItemsThatDoNotContainSelectedTickers } = useTickerFilter();

    /**
     * Clear all state values for this store
     */
    function reset(): void {
        // Reset the tracking variable
        loading.value = false;
        fetching.value = false;
        initialized.value = false;

        // Reset the aggregate collection of all articles
        allArticlesBySlugMap.clear();
        allArticlesByIdMap.clear();
        allArticles.value = [];
    }

    async function initialize(): Promise< void > {
        if ( initialized.value || loading.value ) {
            return;
        }

        const t0: number = performance.now();

        if ( isAuthenticated.value ) {
            loading.value = true;
            try {
                // Wait for the database to open before the first read.
                await cache.ready();

                if ( !await articles.isEmpty() ) {
                    const rows = await articles.all();
                    const loadedArticles: Article[] = rows.map( ( item: NormalizedContent ) => hydrateContent( item, appStore.compositionCtx ) );
                    mergeFilterAndSortArticlesCatalog( loadedArticles );

                    const t1: number = performance.now();
                    logger.info( `Loaded ${ loadedArticles.length } articles from local cache in ${ t1 - t0 } ms` );
                } else {
                    const fetched: Article[] = await refreshArticlesFromServer( formatDateAsISO8601String( DateTime.utc().plus( { days: -90 } ) ) );
                    mergeFilterAndSortArticlesCatalog( fetched );

                    const t1: number = performance.now();
                    logger.log( `Fetching & mapping ${ fetched.length } articles took ${ t1 - t0 } ms` );
                }
            } catch ( err: any ) {
                // A newer build evicted this context: stop quietly, the reload is already in flight.
                if ( err instanceof CacheClosedError ) {
                    return;
                }

                // Not rethrown: every production caller fires initialize() without awaiting it, so a rethrow is an
                // unhandled rejection (tmi-cya #3). Same handling as Webinars/TradeAlerts.
                reset();

                clientStateManager.queueClientError( err, {} );
            } finally {
                initialized.value = allArticles.value.length > 0;
                loading.value = false;
            }
        } else {
            if ( allArticles.value.length > 0 ) {
                return;
            }

            loading.value = true;
            // See Webinars.initialize: hydrateContent commits a non-reactive snapshot, so this must
            // not run before the author/category/product/tag catalogues are populated. Public routes
            // no longer wait on the shell, so join the shared initialization first.
            appStore.initializeAsync()
                .then( () => refreshArticlesFromServer( formatDateAsISO8601String( DateTime.utc().plus( { days: -45 } ) ) ) )
                .then( ( articles: Article[] ) => {
                    mergeFilterAndSortArticlesCatalog( articles );

                    const t1: number = performance.now();
                    logger.log( `Fetching & mapping ${ articles.length } articles took ${ t1 - t0 } ms` );
                } )
                .finally( () => loading.value = false );
        }
    }

    async function fetchArticleBySlugIfMissing( slug: string ): Promise< Article | undefined > {
        // Best case scenario is that the article has already been loaded and is immediately available
        let requestedArticle: Article | undefined = allArticlesBySlugMap.get( slug );
        if ( requestedArticle ) {
            return requestedArticle;
        }

        try {
            fetching.value = true;

            // Issue the article request FIRST, then join the shared initialization while it is in
            // flight. Callers used to await initializeAsync() before calling in here, which put
            // nine catalogue loads on the critical path ahead of the article itself. Only the
            // hydration below actually needs the composition context, so the two overlap freely.
            const articleRequest =
                goFetch< Articles, PersistedQueryPayload >( {
                    serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: '1f2736d6d67c809d2304318cc1c20a74',
                        variables: {
                            skip: 0,
                            limit: 1,
                            contentFilter: {
                                slugs: [ slug ]
                            }
                        }
                    },
                    authToken: authToken.value
                } );

            // hydrateContent resolves the byline, tags and product links from the composition
            // context and commits a plain snapshot, so the catalogues must be loaded by the time we
            // reach it — but not before the request above was sent.
            const [ response ] = await Promise.all( [ articleRequest, appStore.initializeAsync() ] );

            if ( response?.data?.articlesCatalog?.articles ) {
                response.data.articlesCatalog.articles
                    .map( ( a: ContentfulArticle ) => {
                        const normalized = normalizeArticle( a );
                        if ( normalized ) {
                            requestedArticle = hydrateContent( normalized, appStore.compositionCtx );
                            allArticlesByIdMap.set( requestedArticle.contentfulId, requestedArticle as Article );
                            allArticlesBySlugMap.set( requestedArticle.slug, requestedArticle as Article );
                        }
                    } );
            }
        } catch ( e: any ) {
            logger.error( JSON.stringify( e.message ) );
            return undefined;
        } finally {
            fetching.value = false;
        }

        return requestedArticle;
    }

    /**
     * Retrieves an Article object by its slug value or returns undefined if the given
     * slug value is not found within the in-memory collection.
     *
     * @param {string} slug - the unique url-friendly string that identifies the Article
     * @returns {ContentfulItem | undefined} - the Article object or undefined if the slug is not
     * found within the local collection of Article objects
     */
    function articleBySlug( slug: string ): Article | undefined {
        return allArticlesBySlugMap.get( slug );
    }

    /**
     * Retrieves an Article object by its contentful sys.id value or returns undefined if the given
     * id value is not found within the in-memory collection.
     *
     * @param {string} id - the unique contentful sys.id string that identifies the Article
     * @returns {ContentfulItem | undefined} - the Article object or undefined if the id is not
     * found within the local collection of Article objects
     */
    function articleById( id: string ): Article | undefined {
        return allArticlesByIdMap.get( id );
    }

    const chosenArticleCategory = ref< string >( '' );
    function setSelectedArticleCategory( categorySlug: string ) {
        chosenArticleCategory.value = categorySlug;
    }

    const filteredOutCategories = ref< Array< string > >( [] as Array< string > );
    function setFilteredOutCategories( categorySlugs: Array< string > ) {
        filteredOutCategories.value = categorySlugs;
    }

    const articlesNotFilteredOut = computed< Article[] >( () => {
        // If no categories are filtered out, return all articles
        if ( filteredOutCategories.value.length === 0 ) {
            return allArticles.value;
        }

        // Filter out articles that contain any of the filtered-out categories
        return allArticles.value.filter( ( article: Article ) => {
            // Return false if article contains any of the filtered-out categories
            return !article.categories.some( ( category: Category ) =>
                filteredOutCategories.value.includes( category.slug )
            );
        } );
    } );

    const articlesBySelectedCategory = computed< Article[] >( () => {
        let articlesThatAreAssociatedWithTheRequestedCategory: Article[] = [];
        // Lookup the category that will be used to filter out articles that do not have it associated
        const selectedCategory: Category | undefined = categoriesStore.categoryBySlug( chosenArticleCategory.value );
        logger.debug( `Selected Category [name]: ${ selectedCategory?.name }` );
        if ( selectedCategory ) {
            // clear any category filter(s) that previously existed
            includeCategoryFilter.value = [];
            // set the filter criteria
            includeCategoryFilter.value.push( selectedCategory );
            // Perform the filter
            articlesThatAreAssociatedWithTheRequestedCategory = allArticles.value.filter( ( article: Article ) => filterOutItemsThatDoNotContainSelectedCategories( article.categories ) );
        }

        return articlesThatAreAssociatedWithTheRequestedCategory;
    } );

    const numberOfArticles = ref< number >( 0 );
    function setNumberOfArticles( articleCount: number ) {
        numberOfArticles.value = articleCount;
    }

    const nMostRecentlyPublishedArticles = computed< Article[] >( () => {
        logger.info( `computing nArticles => # total articles: ${ allArticles.value.length }, n articles: ${ numberOfArticles.value }` );
        return allArticles.value.slice( 0, numberOfArticles.value );
    } );

    async function refreshArticlesFromServer( latestUtcDate: string ): Promise< Article[] > {
        let retrievedArticles: Article[] = [];
        try {
            const response =
                await goFetch< Articles, PersistedQueryPayload >( {
                    serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: '1f2736d6d67c809d2304318cc1c20a74',
                        variables: {
                            skip: 0,
                            limit: 250,
                            contentFilter: {
                                fromPublishedDateTimeUTC: latestUtcDate
                            }
                        }
                    },
                    authToken: authToken.value
                } );

            if ( null != response?.data?.articlesCatalog?.articles ) {
                response.data.articlesCatalog.articles
                    .map( ( a: ContentfulArticle ) => {
                        const normalized = normalizeArticle( a );
                        if ( normalized ) {
                            retrievedArticles.push( hydrateContent( normalized, appStore.compositionCtx ) );
                        }
                    } );
            }
        } catch( err: any ) {
            // If anything went wrong while attempting to initialize the store, reset it and then handle the specific error
            reset();

            clientStateManager.queueClientError( err, {
                startingDate: latestUtcDate
            } );
        }

        return retrievedArticles;
    }

    /**
     * Merges existing with new (replacing any duplicates), filters out any articles that are not accessible to the
     * current user and sorts (based on article publication date).
     *
     * @param {Article[]} newArticles - the newly loaded collection of Article objects that need to be added
     * @returns {Article[]} - the aggregated collection of Article objects sorted by their publication date
     */
    function mergeFilterAndSortArticlesCatalog( newArticles: Article[] ): void {
        newArticles.forEach( ( newArticle: Article ): void => {
            // console.warn( newArticle );
            if ( typeof newArticle.contentfulId !== 'undefined' ) {
                // Always overwrite whatever was previously in the collection (fucking crazy editing process, why fix it
                // when you can just make everything else more fucking complicated)
                allArticlesByIdMap.set( newArticle.contentfulId, newArticle );
                allArticlesBySlugMap.set( newArticle.slug, newArticle );
            }
        } );

        allArticles.value = [ ...( Array.from( allArticlesBySlugMap.values() )
                // Filter out any content to which the current user has not been granted access
                .filter( ( article: Article ) => {
                    if ( !isFreelyAvailableContent( article.products.map( p => p.slug ) ) ) {
                        return isAuthenticated.value && article.products.some( ( p: Product ) =>
                            !!( p.codes ) && permissionsStore.hasAccessBasedOnShortCode( p.codes! )
                        );
                    }

                    return true;
                } )
                .sort( ( a1: ContentfulItem, a2: ContentfulItem ) =>
                    a2.publishedDate.localeCompare( a1.publishedDate )
                )
        ) ];
    }

    return {
        // ACTIONS
        articleById,
        articleBySlug,
        fetchArticleBySlugIfMissing,
        initialize,
        setFilteredOutCategories,
        setNumberOfArticles,
        setSelectedArticleCategory,
        reset,

        // GETTERS
        allArticles,
        articlesBySelectedCategory,
        articlesNotFilteredOut,
        articleCategories,
        initialized,
        isLoading,
        nMostRecentlyPublishedArticles
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useArticlesStore, import.meta.hot ) );
}
